import { Button } from '@/components/ui/Button';
import { ActorMark } from '@/components/brand/ActorMark';

interface Props {
  error: string | null;
  signInDisabled: boolean;
  onSignIn: () => void;
}

export function LandingPage({ error, signInDisabled, onSignIn }: Props) {
  return (
    <main className="min-h-dvh overflow-hidden bg-[#f3efe7] text-ink-950 dark:bg-ink-950 dark:text-ink-50">
      <section className="relative flex min-h-[86dvh] flex-col overflow-hidden bg-ink-950 text-white">
        <div
          className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_50%_-10%,#cf6446_0,transparent_38%),linear-gradient(90deg,transparent_49.8%,rgba(255,255,255,.08)_50%,transparent_50.2%)]"
          aria-hidden="true"
        />
        <div className="safe-top relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
          <a href="#top" className="flex items-center gap-3 font-bold" aria-label="Line Runner home">
            <ActorMark className="size-11" />
            <span className="text-lg">Line Runner</span>
          </a>
          <Button
            variant="ghost"
            className="border border-white/25 text-white hover:bg-white/10 dark:text-white"
            onClick={onSignIn}
            disabled={signInDisabled}
          >
            Sign in
          </Button>
        </div>

        <div id="top" className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 items-end px-5 pb-10 sm:px-8 sm:pb-14">
          <div className="relative z-10 max-w-2xl pb-2">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-stage-400">
              Rehearsal, on your time
            </p>
            <h1 className="flex items-center gap-3 font-script text-5xl leading-[0.95] sm:gap-5 sm:text-7xl lg:text-8xl">
              <ActorMark className="size-12 shrink-0 sm:size-16 lg:size-20" />
              <span>Line Runner</span>
            </h1>
            <p className="mt-5 max-w-xl text-xl leading-snug text-ink-200 sm:text-2xl">
              Your endlessly patient scene partner. It reads their lines, waits for yours, and
              remembers what needs another run.
            </p>

            {error ? (
              <p className="mt-5 rounded-lg border border-danger-400/30 bg-danger-400/15 px-4 py-3 text-sm text-danger-400">
                {error}
              </p>
            ) : null}

            <Button
              size="lg"
              className="mt-7 min-w-44 shadow-[0_8px_30px_rgba(207,100,70,.3)]"
              onClick={onSignIn}
              disabled={signInDisabled}
            >
              Start rehearsing
              <ArrowIcon />
            </Button>
            <p className="mt-3 text-xs text-ink-400">Your scripts stay private to your account.</p>
          </div>

          <div className="pointer-events-none absolute right-[-1rem] bottom-[-2rem] hidden w-[31rem] opacity-75 sm:block lg:right-4 lg:w-[36rem]">
            <CallingActor />
          </div>
        </div>
      </section>

      <section className="border-b border-ink-200 bg-[#f3efe7] px-5 py-14 dark:border-ink-800 dark:bg-ink-950 sm:px-8 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="font-script text-3xl leading-tight sm:text-4xl">
            From script to off-book, without borrowing another actor.
          </p>
          <div className="mt-10 grid border-y border-ink-300 dark:border-ink-700 md:grid-cols-3 md:divide-x md:divide-ink-300 dark:md:divide-ink-700">
            <Feature number="01" title="Bring the script">
              Import PDF, DOCX, or text, then choose your role.
            </Feature>
            <Feature number="02" title="Hear every cue">
              Character voices read the scene and pause exactly where you enter.
            </Feature>
            <Feature number="03" title="Know what sticks">
              Track misses, drill weak lines, and watch your recall improve.
            </Feature>
          </div>
        </div>
      </section>

      <section className="bg-brand-600 px-5 py-12 text-white sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <p className="font-script text-3xl">Places, please.</p>
            <p className="mt-1 text-sm text-white/75">Your next cue is waiting.</p>
          </div>
          <Button size="lg" className="bg-white text-brand-700 hover:bg-ink-100" onClick={onSignIn} disabled={signInDisabled}>
            Sign in to Line Runner
          </Button>
        </div>
      </section>
    </main>
  );
}

function Feature({ number, title, children }: { number: string; title: string; children: string }) {
  return (
    <div className="border-b border-ink-300 py-7 last:border-b-0 dark:border-ink-700 md:border-b-0 md:px-8 md:first:pl-0 md:last:pr-0">
      <p className="text-brand-600 dark:text-brand-400 text-xs font-bold tracking-[0.18em]">{number}</p>
      <h2 className="mt-3 text-lg font-bold">{title}</h2>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink-600 dark:text-ink-300">{children}</p>
    </div>
  );
}

function CallingActor() {
  return (
    <svg viewBox="0 0 520 590" fill="none" aria-hidden="true">
      <path d="M350 67c47-39 126-23 148 26 17 38-6 82-54 97l6 54-61-38c-43-1-76-26-84-57" stroke="#f3efe7" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="352" y="137" fill="#f3efe7" fontFamily="Georgia, serif" fontSize="35" fontStyle="italic">LINE!</text>
      <circle cx="247" cy="112" r="39" stroke="#f3efe7" strokeWidth="8" />
      <path d="M221 108c7-8 14-8 21 0m18 0c7-8 14-8 21 0m-42 24c12 8 24 8 36-1" stroke="#f3efe7" strokeWidth="6" strokeLinecap="round" />
      <path d="M247 153c-10 68-13 129-7 183m-1-124-101 81m104-77 91 83" stroke="#f3efe7" strokeWidth="10" strokeLinecap="round" />
      <path d="m137 292-42-2m238 9 35-31m-128 68-76 173m77-173 91 168" stroke="#f3efe7" strokeWidth="10" strokeLinecap="round" />
      <path d="m89 280 13 11-13 10m280-44 8 14 14-7" stroke="#f3efe7" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M205 171c27 12 54 12 81 0" stroke="#cf6446" strokeWidth="8" strokeLinecap="round" />
      <path d="M219 82c6-22 42-35 64-11" stroke="#f3efe7" strokeWidth="8" strokeLinecap="round" />
      <path d="M57 512c109 15 215 15 414 0" stroke="#f3efe7" strokeWidth="5" strokeLinecap="round" strokeDasharray="3 16" opacity=".5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="size-5" aria-hidden="true">
      <path d="M5 12h14m-5-5 5 5-5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}